<template>
  <v-container fluid class="pa-0">
    <!-- Non-Warehouse Mode Fields -->
    <template v-if="!warehouseModule">
      <v-row>
        <v-col cols="12" class="pb-0">
          <v-select
            multiple
            outlined
            dense
            clearable
            label="Odaberi prodajna mjesta"
            :items="locations"
            item-text="name"
            item-value="id"
            v-model="localData.selectedLocations"
            @change="handleChange"
            hide-details
            class="filter-select"
          ></v-select>
        </v-col>
      </v-row>

      <v-row>
        <v-col cols="7" class="pb-0">
          <v-select
            multiple
            outlined
            dense
            label="Odaberite kategoriju"
            :items="categories"
            return-object
            item-text="name"
            v-model="localData.selectedCategories"
            @change="handleChange"
            :loading="categoryProcessing"
            hide-details
            class="filter-select"
          ></v-select>
        </v-col>
        <v-col cols="5" class="d-flex align-center pb-0">
          <v-btn
            x-small
            style="z-index: 4"
            plain
            text
            @click="openAddCategoryForm"
          >
            <v-icon>mdi-plus</v-icon>
            <span>Dodaj kategoriju</span>
          </v-btn>
        </v-col>
      </v-row>

      <v-row>
        <v-col cols="8">
          <v-combobox
            outlined
            autocomplete="off"
            v-model="localData.selectedPrices"
            :items="[]"
            dense
            label="Cijene za artikla u valutama"
            multiple
            chips
            clearable
            @click="openAddPriceForm"
            @keydown.enter="openAddPriceForm"
            @keydown="preventTextInput($event)"
            hide-details
            class="filter-select"
          >
            <template v-slot:selection="{ item }">
              <v-chip
                class="mt-1 mr-0"
                close
                @click="editPrice(item)"
                @click:close="removePrice(item)"
              >
                <div v-if="localData.manualPriceInput">
                  {{ item.currency }}
                </div>
                <div v-else>
                  {{ item.price | money(1, item.currency) }}
                </div>
              </v-chip>
            </template>
          </v-combobox>
        </v-col>
        <v-col cols="4" class="d-flex align-center">
          <v-checkbox
            v-model="localData.manualPriceInput"
            label="Upis cijene na blagajni"
            @change="handleChange"
            hide-details
            dense
          ></v-checkbox>
        </v-col>
      </v-row>
    </template>

    <!-- Warehouse Module Fields -->
    <template v-else>
      <!-- Sales Locations -->
      <!-- Sales Locations + Categories + Organizational Unit -->
      <v-row>
        <v-col cols="3" class="pb-0">
          <v-select
            multiple
            outlined
            dense
            clearable
            label="Prodajna mjesta"
            :items="locations"
            item-text="name"
            item-value="id"
            v-model="localData.selectedSalesLocations"
            @change="handleChange"
            hide-details
            class="filter-select"
          >
            <template v-slot:selection="{ item, index }">
              <span v-if="index === 0" class="text-caption grey--text">
                {{ localData.selectedSalesLocations.length }} odabrano
              </span>
            </template>
          </v-select>
        </v-col>
        <v-col cols="4" class="pb-0">
          <v-select
            multiple
            outlined
            dense
            label="Kategorija"
            :items="categories"
            return-object
            item-text="name"
            v-model="localData.selectedCategories"
            @change="handleChange"
            :loading="categoryProcessing"
            hide-details
            class="filter-select"
          >
            <template v-slot:selection="{ item, index }">
              <span v-if="index === 0" class="text-caption grey--text">
                {{ localData.selectedCategories.length }} odabrano
              </span>
            </template>
            <template v-slot:append-outer>
              <v-btn
                x-small
                icon
                @click="openAddCategoryForm"
                class="mt-n1"
              >
                <v-icon small>mdi-plus</v-icon>
              </v-btn>
            </template>
          </v-select>
        </v-col>
        <v-col cols="3" class="pb-0">
          <v-select
            outlined
            dense
            label="Ustrojstvena jedinica"
            :items="orgUnits"
            item-text="name"
            item-value="id"
            v-model="localData.selectedOrgUnit"
            @change="handleChange"
            hide-details
            class="filter-select"
          ></v-select>
        </v-col>
      </v-row>

      <!-- Main Price Input -->
      <v-row>
        <v-col cols="8">
          <v-combobox
            outlined
            autocomplete="off"
            v-model="localData.selectedPrices"
            :items="[]"
            dense
            label="Cijene za artikl"
            multiple
            chips
            clearable
            @click="openAddPriceForm"
            @keydown.enter="openAddPriceForm"
            @keydown="preventTextInput($event)"
            hide-details
            class="filter-select"
          >
            <template v-slot:selection="{ item }">
              <v-chip
                class="mt-1 mr-0"
                close
                @click="editPrice(item)"
                @click:close="removePrice(item)"
              >
                <div v-if="localData.manualPriceInput">
                  {{ item.currency }}
                </div>
                <div v-else>
                  {{ item.price | money(1, item.currency) }}
                </div>
              </v-chip>
            </template>
          </v-combobox>
        </v-col>
        <v-col cols="4" class="d-flex align-center">
          <v-checkbox
            v-model="localData.manualPriceInput"
            label="Upis cijene na blagajni"
            @change="handleChange"
            hide-details
            dense
          ></v-checkbox>
        </v-col>
      </v-row>

      <!-- Unit Pricing - Flat List -->
      <v-row v-if="localData.measureUnits && localData.measureUnits.length > 0">
        <v-col cols="12">
          <v-divider class="my-4"></v-divider>
          <h3 class="text-subtitle-1 mb-3">Cijene po jedinicama mjere</h3>

          <!-- Each unit in its own row -->
          <v-card
            v-for="(unit, idx) in localData.measureUnits"
            :key="idx"
            outlined
            class="mb-3 pa-4"
          >
            <v-row>
              <v-col cols="3">
                <v-text-field
                  outlined
                  disabled
                  dense
                  v-model="unit.storage_unit"
                  label="Jedinica"
                  hide-details
                  class="filter-select"
                ></v-text-field>
              </v-col>
              <v-col cols="2">
                <v-text-field
                  outlined
                  dense
                  disabled
                  v-model="unit.storage_amount"
                  label="Količina"
                  hide-details
                  class="filter-select"
                ></v-text-field>
              </v-col>
              <v-col cols="5">
                <v-combobox
                  outlined
                  autocomplete="off"
                  v-model="unit.prices"
                  :items="[]"
                  dense
                  label="Cijene"
                  multiple
                  chips
                  clearable
                  @click="openAddPriceFormForUnit(unit)"
                  :error="hasUnitPriceError(idx)"
                  hide-details
                  class="filter-select"
                >
                  <template v-slot:selection="{ item }">
                    <v-chip class="mt-1 mr-0" small close @click:close="removePriceFromUnit(unit, item)">
                      <div v-if="localData.manualPriceInput">
                        {{ item.currency }}
                      </div>
                      <div v-else>
                        {{ item.price | money(1, item.currency) }}
                      </div>
                    </v-chip>
                  </template>
                </v-combobox>
              </v-col>
              <v-col cols="2" class="d-flex align-center justify-center">
                <v-checkbox
                  v-model="unit.active"
                  label="Aktivan"
                  hide-details
                  dense
                  @change="handleChange"
                ></v-checkbox>
              </v-col>
            </v-row>
          </v-card>
        </v-col>
      </v-row>
    </template>

    <!-- Hidden dialogs -->
    <add-category
      ref="addCategory"
      @processing="setCategoryProcessing"
      @add="onAddCategory"
    ></add-category>

    <add-price
      ref="addPrice"
      :taxes="taxes"
      :isManual="localData.manualPriceInput"
      @add="onAddPrice"
    ></add-price>
  </v-container>
</template>

<script>
import state from '@/state'
import { df } from '@/plugins/firebase'
import { clone } from '@/plugins/utils'
import AddCategory from '@/modules/company/components/AddCategory'
import AddPrice from '@/modules/point-of-sale/components/AddPrice'

export default {
  name: 'PriceListFields',
  components: { AddCategory, AddPrice },
  props: {
    value: {
      type: Object,
      required: true
    },
    warehouseModule: {
      type: Boolean,
      default: false
    },
    itemId: {
      type: String,
      default: undefined
    },
    locations: {
      type: Array,
      default: () => []
    },
    units: {
      type: Array,
      default: () => []
    },
    validationErrors: {
      type: Array,
      default: () => []
    },
    taxGroup: {
      type: String,
      default: undefined
    }
  },
  data: () => ({
    localData: {
      manualPriceInput: false,
      selectedCategories: [],
      selectedPrices: [],
      selectedLocations: [],
      selectedSalesLocations: [],
      selectedOrgUnit: undefined,
      unitPrices: [],
      measureUnits: [],
      saleUnit: undefined,
      rounding: false,
      margins: [],
      normRows: []
    },
    categories: [],
    categoryProcessing: false,
    taxes: [],
    orgUnits: [],
    listeners: [],
    showMarginDialog: false,
    margin: 0,
    marginPercent: 0,
    currency: 'EUR',
    showMargin: false,
    pendingCategoryName: null, // Track newly created category to auto-select it
    currentEditingUnit: null, // Track which unit is being edited for prices
    normHeaders: [
      { text: 'Artikl', value: 'name' },
      { text: 'Potrebna količina', value: 'quantity' },
      { text: 'Nabavna cijena za količinu', value: 'purchasePrice' }
    ]
  }),
  watch: {
    value: {
      handler (newVal) {
        if (newVal) {
          this.localData = clone(newVal)
        }
      },
      deep: true,
      immediate: true
    },
    locations: {
      handler (newLocations) {
        // Auto-select all locations by default (only if none selected yet)
        if (newLocations && newLocations.length > 0) {
          if (!this.warehouseModule && this.localData.selectedLocations.length === 0) {
            this.localData.selectedLocations = newLocations.map(loc => loc.id)
            this.handleChange()
          }
          if (this.warehouseModule && this.localData.selectedSalesLocations.length === 0) {
            this.localData.selectedSalesLocations = newLocations.map(loc => loc.id)
            this.handleChange()
          }
        }
      },
      immediate: true
    },
    units: {
      handler (newUnits) {
        // Sync units from parent to localData.measureUnits
        if (newUnits && newUnits.length > 0 && this.warehouseModule) {
          // Map from newUnits structure to measureUnits structure
          // Filter only units marked as "Prodaje se" (sellingItem === true)
          this.localData.measureUnits = newUnits
            .filter(unit => unit.sellingItem === true)
            .map(unit => {
              return {
                storage_unit: unit.customItemUnit || unit.storage_unit,
                storage_amount: unit.customItemAmount || unit.storage_amount,
                default_sale: unit.defaultSale || unit.default_sale || false,
                active: true, // Already filtered by sellingItem
                prices: unit.prices || [],
                id: unit.id || unit.value
              }
            })

          // Merge prices from loaded price list data (unitPrices)
          if (this.localData.unitPrices && this.localData.unitPrices.length > 0) {
            this.localData.measureUnits.forEach(unit => {
              // Find matching unit in unitPrices by unit_id or storage_unit
              const loadedUnitPrice = this.localData.unitPrices.find(up =>
                up.unit_id === unit.id || up.storage_unit === unit.storage_unit
              )
              if (loadedUnitPrice && loadedUnitPrice.prices) {
                unit.prices = loadedUnitPrice.prices
              }
            })
          }

          // Set the first defaultSale unit as saleUnit if not already set
          if (!this.localData.saleUnit && this.localData.measureUnits.length > 0) {
            let defaultSaleUnit = this.localData.measureUnits.find(u => u.default_sale === true)

            // If no default sale unit, use the first unit
            if (!defaultSaleUnit) {
              defaultSaleUnit = this.localData.measureUnits[0]
              defaultSaleUnit.default_sale = true // Mark it as default
            }

            if (defaultSaleUnit) {
              this.localData.saleUnit = {
                ...defaultSaleUnit,
                prices: defaultSaleUnit.prices || []
              }
            }
          }

          this.handleChange()
        }
      },
      deep: true,
      immediate: true
    },
    'value.unitPrices': {
      handler (newUnitPrices) {
        // When unit prices are loaded (async), merge them with existing measureUnits
        if (newUnitPrices && newUnitPrices.length > 0 && this.localData.measureUnits && this.localData.measureUnits.length > 0) {
          this.localData.measureUnits.forEach(unit => {
            const loadedUnitPrice = newUnitPrices.find(up =>
              up.unit_id === unit.id || up.storage_unit === unit.storage_unit
            )
            if (loadedUnitPrice && loadedUnitPrice.prices) {
              unit.prices = loadedUnitPrice.prices
            }
          })
        }
      },
      deep: true
    }
  },
  mounted () {
    this.currency = 'EUR'
    this.loadCategories()
    this.loadTaxes()
    if (this.warehouseModule) {
      this.loadOrgUnits()
    }
  },
  beforeDestroy () {
    this.detachListeners()
  },
  methods: {
    handleChange () {
      this.$emit('input', clone(this.localData))
    },
    preventTextInput (e) {
      if (e.keyCode !== 9 && e.keyCode !== 27) {
        this.openAddPriceForm()
        e.preventDefault()
      }
    },
    openAddCategoryForm () {
      this.$refs.addCategory.open()
    },
    openAddPriceForm () {
      this.currentEditingUnit = null // Reset - adding to main prices
      // Pass tax_group information to AddPrice modal for default PDV selection
      const newItem = this.taxGroup ? { item: { tax_group: this.taxGroup } } : null
      this.$refs.addPrice.open(null, null, null, newItem)
    },
    onAddPrice (priceItem) {
      // If editing a specific unit, add price to that unit
      if (this.currentEditingUnit) {
        const unit = this.localData.measureUnits.find(u => u.id === this.currentEditingUnit.id)
        if (unit) {
          if (!unit.prices) {
            unit.prices = []
          }
          // Check if currency already exists for this unit
          const existingIndex = unit.prices.findIndex(p => p.currency === priceItem.currency)
          if (existingIndex >= 0) {
            unit.prices[existingIndex] = priceItem
          } else {
            unit.prices.push(priceItem)
          }
        }
        this.currentEditingUnit = null // Reset
      } else {
        // Adding to main selectedPrices (default behavior)
        if (
          this.localData.selectedPrices.some((item) => item.currency === priceItem.currency)
        ) {
          const sameItem = this.localData.selectedPrices.find(
            (it) => it.currency === priceItem.currency
          )
          this.localData.selectedPrices = this.localData.selectedPrices.map((item) => {
            item.price = item === sameItem ? priceItem.price : item.price
            return item
          })
        } else {
          this.localData.selectedPrices.push(priceItem)
        }
      }
      this.handleChange()
    },
    removePrice (priceItem) {
      this.localData.selectedPrices = this.localData.selectedPrices.filter(
        (item) => item !== priceItem
      )
      this.handleChange()
    },
    removePriceFromUnit (unit, priceItem) {
      if (unit.prices) {
        unit.prices = unit.prices.filter(p => p !== priceItem)
        this.handleChange()
      }
    },
    editPrice (priceItem) {
      this.$refs.addPrice.open(priceItem)
    },
    openAddPriceFormForUnit (unit) {
      this.currentEditingUnit = unit
      // Pass tax_group information to AddPrice modal for default PDV selection
      const newItem = this.taxGroup ? { item: { tax_group: this.taxGroup } } : null
      this.$refs.addPrice.open(null, null, null, newItem)
    },
    sumNormPrices () {
      if (!this.localData.normRows || this.localData.normRows.length === 0) {
        return 0
      }
      return this.localData.normRows.reduce((sum, row) => sum + (row.purchasePrice || 0), 0)
    },
    hasUnitPriceError (idx) {
      return this.validationErrors.includes(`unit_${idx}_prices_missing`)
    },
    setCategoryProcessing (processing) {
      this.categoryProcessing = processing
    },
    onAddCategory (categoryData) {
      // Store the name of the newly created category
      console.log('📝 Nova kategorija kreirana:', categoryData.name)
      this.pendingCategoryName = categoryData.name
    },
    loadCategories () {
      this.listeners.push(
        df
          .doc(`company_categories/${state.getCurrentCompany().id}`)
          .onSnapshot((doc) => {
            if (
              doc &&
              doc.data() &&
              doc.data().categories &&
              Object.keys(doc.data().categories).length > 0
            ) {
              const categories = Object.keys(doc.data().categories).map(
                (key) => {
                  return doc.data().categories[key]
                }
              )
              this.categories = clone(categories.sort((a, b) => a.name.localeCompare(b.name)))
              this.categoryProcessing = false

              // Auto-select newly created category
              if (this.pendingCategoryName) {
                const newCategory = this.categories.find(
                  cat => cat.name.toLowerCase() === this.pendingCategoryName.toLowerCase()
                )
                if (newCategory) {
                  // Check if not already selected
                  const alreadySelected = this.localData.selectedCategories.some(
                    cat => cat.id === newCategory.id
                  )
                  if (!alreadySelected) {
                    this.localData.selectedCategories.push(newCategory)
                    this.handleChange()
                  }
                  this.pendingCategoryName = null // Reset
                }
              }
            }
          })
      )
    },
    loadTaxes () {
      this.listeners.push(
        df.doc('project_constants/taxes').onSnapshot((doc) => {
          if (doc.data() && doc.data().pdv && doc.data().pdv.length > 0) {
            this.taxes = doc.data().pdv.map((tax) => {
              return {
                ...tax,
                ...{
                  disabled:
                    !state.getCurrentCompany().is_taxed && tax.rate !== 0
                }
              }
            })
          }
        })
      )
    },
    loadOrgUnits () {
      const posId = state.getPointOfSale()?.id
      if (!posId) return

      df.collection(`location_units/${posId}/units`)
        .get()
        .then((documentSnapshots) => {
          if (documentSnapshots.docs && !documentSnapshots.docs.empty) {
            const orgUnits = []
            documentSnapshots.docs.forEach((orgUnit) => {
              orgUnits.push(orgUnit.data())
            })
            this.orgUnits = clone(orgUnits.sort((a, b) => a.name.localeCompare(b.name)))
          }
        })
    },
    detachListeners () {
      this.listeners.forEach((listener) => {
        listener()
      })
      this.listeners = []
    }
  }
}
</script>

<style scoped>
.margin-ok {
  background: linear-gradient(135deg, #43a047 0%, #66bb6a 100%);
}

.margin-not-ok {
  background: linear-gradient(135deg, #e53935 0%, #ef5350 100%);
}

/* Filter Select label and placeholder styling */
.filter-select >>> label {
  font-size: 12px !important;
  font-weight: 600 !important;
  color: #3989C9 !important;
}

.filter-select >>> input::placeholder,
.filter-select >>> textarea::placeholder {
  color: #3989C9 !important;
  opacity: 0.7 !important;
}
</style>
